// src/utils/date.ts

import { DateTime } from 'luxon';

export const DEFAULT_PROJECT_TZ = 'Asia/Seoul';

function isDateOnlyString(value: string) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toProjectDateTimeValue(value: string | Date, zone = DEFAULT_PROJECT_TZ): DateTime | null {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return DateTime.fromJSDate(value).setZone(zone);
    }

    if (!value) return null;
    if (isDateOnlyString(value)) {
        const dt = DateTime.fromISO(value, { zone }).startOf('day');
        return dt.isValid ? dt : null;
    }

    const utc = DateTime.fromISO(value, { zone: 'utc' });
    if (utc.isValid) return utc.setZone(zone);

    const local = DateTime.fromJSDate(new Date(value)).setZone(zone);
    return local.isValid ? local : null;
}

export function getProjectToday(zone = DEFAULT_PROJECT_TZ): string {
    return DateTime.now().setZone(zone).toISODate()!;
}

export function formatProjectDateOnly(
    value?: string | Date | null,
    zone = DEFAULT_PROJECT_TZ,
    fallback = '',
): string {
    if (!value) return fallback;
    if (typeof value === 'string' && isDateOnlyString(value)) return value;

    const dt = toProjectDateTimeValue(value, zone);
    return dt?.toISODate() ?? fallback;
}

export function formatProjectDateTime(
    value?: string | Date | null,
    zone = DEFAULT_PROJECT_TZ,
    format = 'yyyy-MM-dd HH:mm',
    fallback = '',
): string {
    if (!value) return fallback;
    const dt = toProjectDateTimeValue(value, zone);
    return dt?.toFormat(format) ?? fallback;
}

/**
 * YYYY-MM-DD 또는 임의의 날짜 문자열을 로케일 형식으로 표시합니다.
 * - 입력이 'YYYY-MM-DD'이면 그대로 반환(일관성 유지)
 * - 파싱 불가 시 원문 문자열을 반환
 */
export function fmtLocalDateDefault(s?: string | null, locale = 'ko-KR'): string {
    if (!s) return '';
    if (isDateOnlyString(s)) return s;
    const dt = toProjectDateTimeValue(s, DEFAULT_PROJECT_TZ);
    if (!dt) return String(s ?? '');
    return dt.setLocale(locale).toLocaleString(DateTime.DATE_MED); // 예: 2025. 10. 28.
}

/**
 * 날짜 문자열을 "YYYY.MM.DD" 형식으로 반환합니다.
 * - 'YYYY-MM-DD' → 점(.)으로 치환
 * - 그 외 문자열은 Date.parse → ISO 자르기(UTC) → 점(.) 치환
 */
export function fmtLocaleDateCompact(s?: string | null): string {
    if (!s) return '';
    return formatProjectDateOnly(s, DEFAULT_PROJECT_TZ, '').replace(/-/g, '.');
}

/**
 * Date → "YYYY.MM.DD"
 */
export function fmtCompact(d?: Date | null): string {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
}

/* ------------------------------------------------------------------ */
/* ISO 주차/기준일 유틸                                                */
/* ------------------------------------------------------------------ */

/**
 * ISO 주차 기준(월요일 시작)으로 주의 시작(월요일 00:00:00)을 반환합니다.
 */
export function startOfISOWeek(d: Date): Date {
    const day = d.getDay(); // Sun=0 … Sat=6
    const diffFromMon = (day + 6) % 7; // Mon=0 … Sun=6
    const res = new Date(d);
    res.setDate(d.getDate() - diffFromMon);
    res.setHours(0, 0, 0, 0);
    return res;
}

/**
 * d에 n일을 더한 새 Date를 반환합니다. (불변)
 */
export function addDays(d: Date, n: number): Date {
    const res = new Date(d);
    res.setDate(d.getDate() + n);
    return res;
}

/**
 * 주간 기준 금요일(ISO 주의 월요일 + 4일)을 반환합니다.
 */
export function fridayOfWeek(d: Date): Date {
    const monday = startOfISOWeek(d);
    return addDays(monday, 4); // Mon + 4 = Fri
}

/**
 * Date → "YYYY-MM-DD"
 */
export function toISODate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * 문자열을 Date로 파싱합니다. 지원 형식:
 * - "YYYY-MM-DD" → 로컬 자정
 * - "YYYY.MM.DD" → 로컬 자정
 * - 그 외 → Date.parse 결과(유효하지 않으면 null)
 */
export function parseISO(s?: string | null): Date | null {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00`);
    if (/^\d{4}\.\d{2}\.\d{2}$/.test(s)) return new Date(s.replace(/\./g, '-') + 'T00:00:00');
    const ms = Date.parse(s);
    return Number.isFinite(ms) ? new Date(ms) : null;
}

/* ------------------------------------------------------------------ */
/* (선택) 과도기 호환용 별칭                                           */
/*  - 기존 코드에서 이름 충돌/이관 중이면 아래 별칭을 임시로 사용하세요. */
/*  - 마이그레이션 완료 후 제거 권장.                                  */
/* ------------------------------------------------------------------ */

/**
 * YYYY-MM-DD 또는 기타 문자열을 UTC 시간으로 변환
 */
export function toRFC3339DateOnlyUTC(s?: string | null): string | null {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        // YYYY-DD-MM 형식이면
        return new Date(`${s}T00:00:00.000Z`).toISOString(); // toISOString은 항상 UTC로 반환. toLocaleString은 locale로
    }
    const d = parseISO(s);
    return d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString() : null;
}

/**
 * 문자열 "YYYY-MM-DD" -> UTC ISO
 * 1) 데이트피커(input 태그)에서 받은 날짜 문자열 "2025-12-15"
 * 2) 시작점으로 KST 자정 지정. "2025-12-15" + T00:00:00.000+09:00
 * 3) new Date()로 UTC 객체 생성. 이 때 날짜는 2025-12-14T15:00:00.000Z로 바뀜 (하루전이라는 것 주의할 것)
 * 4) toISOString() 함수로 문자열로 변환 "2025-12-14T15:00:00.000Z"
 * 5) 이대로 백엔드 전송하여 z.string().datetime()으로 검증
 * 6) datetimetz 형식으로 데이터베이스에 저장
 */
export function dateOnlyToUtcIso(dateStr?: string | null): string | null {
    // dateStr = '2025-12-15'와 같이 <input type="date">로 부터 생성된 문자열
    if (!dateStr) return null;
    // KST 0시 0분으로 기준날짜의 시작점 지정
    return new Date(`${dateStr}T00:00:00.000+09:00`).toISOString();
    // new Date()는 UTC 기준. '2025-12-14:15:00:00.000Z'가 됨.
    // 데이터베이스에 위와 같이 저장됨.
    // 프론트에서 보여줄 때 KST로 변환해서 보여줘야 함.
}

/**
 * UTC ISO -> "YYYY-MM-DD" (KST 기준)
 * 예) "2025-12-14T15:00:00.000Z" -> "2025-12-15"
 */
export function utcIsoToDateOnlyKst(isoStr: string | null): string | null {
    if (!isoStr) return null;
    return formatProjectDateOnly(isoStr, DEFAULT_PROJECT_TZ, '') || null;
}

// 0=Sun...6=Sat 기준. 해당 ISO 주(월~일) 안의 anchorDow(00:00) 날짜 반환
export function weekAnchorOf(date: Date, anchorDow: number): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const monday = startOfISOWeek(d);

    // anchorDow(0..6) -> ISO(1..7)
    const isoDow = anchorDow === 0 ? 7 : anchorDow;
    const offset = isoDow - 1; // Mon=0

    const a = addDays(monday, offset);
    a.setHours(0, 0, 0, 0);
    return a;
}

// 입력 주차 기준: 해당 날짜가 anchor를 이미 지났으면 다음 anchor를, 아니면 이번 anchor를 반환한다.
export function activeWeekAnchorOf(date: Date, anchorDow: number): Date {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);

    const anchor = weekAnchorOf(dayStart, anchorDow);
    if (anchor.getTime() < dayStart.getTime()) {
        anchor.setDate(anchor.getDate() + 7);
    }

    anchor.setHours(0, 0, 0, 0);
    return anchor;
}

export function getProjectActiveWeekAnchorDateOnly(
    now: Date = new Date(),
    anchorDow: number,
    zone = DEFAULT_PROJECT_TZ,
): string {
    const local = DateTime.fromJSDate(now).setZone(zone).startOf('day');
    const isoDow = anchorDow === 0 ? 7 : anchorDow;
    let anchor = local.startOf('week').plus({ days: isoDow - 1 });
    if (anchor < local) anchor = anchor.plus({ days: 7 });
    return anchor.toISODate()!;
}

export function dowLabelKoShort(dow: number) {
    return ['일', '월', '화', '수', '목', '금', '토'][dow] ?? '';
}

/**
 * 이 아랫부분만 사용함. 일정 시간 후 윗 부분은 전부 제거할 것.
 */

// 시작시점 계산: 사용자가 선택한 날짜의 '가장 빠른 시각'을 구한다.
export function toUtcStart(dateOnly: string, zone: string): string {
    return DateTime.fromISO(dateOnly, { zone }) // 1. '2026-02-04'를 해당 타임존(서울)의 00:00:00으로 해석합니다.
        .startOf('day') // 2. 해당 날짜의 시작 시점(00:00:00.000)임을 명시합니다. (이미 00시라면 변화 없음)
        .toUTC() // 3. 이를 UTC로 변환합니다. (서울 04일 00시는 UTC로 03일 15:00:00입니다.)
        .toISO()!; // 4. 표준 문자열로 반환합니다. -> '2026-02-03T15:00:00.000Z'
}

// 마감시점 계산: 사용자가 선택한 날짜의 '가장 늦은 시각'을 구한다.
export function toUtcDeadline(dateOnly: string, zone: string): string {
    return DateTime.fromISO(dateOnly, { zone }) // 1. '2026-02-04'를 해당 타임존(서울)의 00:00:00으로 해석합니다.
        .endOf('day') // 2. 해당 날짜의 마지막 시점(23:59:59.999)으로 시각을 변경합니다.
        .toUTC() // 3. 이를 UTC로 변환합니다. (서울 04일 23:59는 UTC로 04일 14:59:59입니다.)
        .toISO()!; // 4. 표준 문자열로 반환합니다. -> '2026-02-04T14:59:59.999Z'
}

// 단순 날짜 추출: 서버의 시점 데이터를 프로젝트의 날짜(YYYY-MM-DD)로만 뽑아낸다.
export function toProjectDateOnly(utcIso?: string | null, zone?: string): string {
    return formatProjectDateOnly(utcIso, zone ?? DEFAULT_PROJECT_TZ);
}

// 상세 정보 포맷팅: 화면에 표시할 다양한 시간 정보를 객체 형태로 반환한다.
export function formatToProjectTime(utcIso: string, zone: string, format = 'yyyy-MM-dd HH:mm') {
    // 1. UTC 시점을 읽어서 프로젝트 타임존으로 시계 바늘을 맞춥니다.
    const dt = toProjectDateTimeValue(utcIso, zone);
    if (!dt) {
        return {
            display: '',
            dateOnly: '',
            offsetName: '',
        };
    }

    return {
        display: dt.toFormat(format), // 2. 지정한 형식대로 출력합니다. (예: "2026-02-04 23:59")
        dateOnly: dt.toISODate()!, // 3. "2026-02-04" 날짜 문자열만 따로 보관합니다.
        offsetName: dt.offsetNameShort, // 4. "KST" 또는 "GMT+9" 같은 타임존 약어를 가져옵니다.
    };
}

// ✅ DateOnly: YYYY-MM-DD만 유지
export function normalizeDateOnly(v: any): string | null | undefined {
    if (v === undefined) return undefined;
    if (v === null || v === '') return null;

    if (typeof v === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // 그대로
        return null;
    }

    return null;
}

// ✅ Instant: ISO datetime만 (서버 생성/사용자 입력 instant 용)
export function normalizeInstant(v: any): string | null | undefined {
    if (v === undefined) return undefined;
    if (v === null || v === '') return null;

    if (v instanceof Date) return v.toISOString();

    if (typeof v === 'string') {
        // ISO datetime만 정규화 (date-only는 여기서 null 처리)
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d.toISOString();
        return null;
    }

    return null;
}
